import { createContext, useRef, type ReactNode } from 'react';
import { createEagGridStore, type EagGridState, type EagGridStore } from './createEagGridStore';

/**
 * React context that holds the `EagGridStore` instance for the current grid.
 *
 * Access it via `useEagGridStore()` — do **not** consume this context directly.
 * A `null` default value is intentional: `useEagGridStore` will throw a
 * descriptive error if a component attempts to read the store outside a provider.
 */
export const EagGridStoreContext = createContext<EagGridStore | null>(null);
type EagGridStoreProviderProps = {
  children: ReactNode;
  initialState?: Partial<EagGridState>;
};

/**
 * Provides an isolated `EagGridStore` to the entire EagGrid component subtree.
 *
 * ## Prop-drilling avoidance
 *
 * This provider is the cornerstone of EagGrid's architecture.  Instead of
 * threading state (filter values, active panel, loading flag, selection, drag
 * state, …) as props through every intermediate layer, we put all shared state
 * in the store and let leaf components subscribe to exactly the slice they need.
 *
 * Concrete examples of what would otherwise require prop drilling:
 *
 * - `filterValues` — needed by Sidebar > FilterPanel > FilterCategoryMenu,
 *   FilterContentArea and the sidebar filter system.
 * - `activePanel` — controls sidebar visibility; toggled from GridHeader but
 *   read by Sidebar and indirectly by FilterPanel's close button.
 * - `activeDragId` / `overColumnId` / `dropSide` — needed by HeaderCell
 *   and GridCell 4 levels deep without any intermediate component caring.
 *
 * ## Instance isolation
 *
 * The store is created **once** via `useRef` to survive re-renders.  Each
 * mounted `<EagGrid>` gets its own provider and therefore its own store
 * instance — multiple grids on the same page are fully independent.
 */
export function EagGridStoreProvider(props: EagGridStoreProviderProps) {
  /**
   * Lazy-init ref pattern: create the store exactly once on first render.
   * This is the standard pattern recommended by Zustand and React docs for
   * one-time initialization of non-rendering values.
   *
   * The React Compiler flags this as "Cannot access refs during render" because
   * it treats all ref.current reads/writes as side effects. However, this is a
   * safe idempotent initialization — the ref is written at most once and the value
   * never changes. The React team has acknowledged this as a known false positive.
   *
   * @see https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
   */
  const storeRef = useRef<EagGridStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createEagGridStore(props.initialState);
  }
  return (
    // eslint-disable-next-line react-hooks/refs -- storeRef.current is set once on init; this Provider value never changes
    <EagGridStoreContext.Provider value={storeRef.current}>
      {props.children}
    </EagGridStoreContext.Provider>
  );
}